import { IconButton } from '@mui/material'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import EjercicioSelector from '../../../../components/form/EjercicioSelector/EjercicioSelector'
import { format, formatForExport } from '../../../../utils/math'
import { downloadCSVFile, getCSVContent } from '../../../../utils/csv'
import PageLoading from '../../../../components/PageLoading/PageLoading'
import InformationTable from '../../../../components/Table/InformationTable'
import TableHeader from '../../../../components/TableHeader/TableHeader'
import { useNotification } from '../../../../context/NotificationManager/NotificationManager'
import AcademiaSelector from '../../../../components/form/AcademiaSelector/AcademiaSelector'
import { getAuth } from '../../../../utils/auth'
import { dateToMonth } from '../../../../utils/date'
import { getTotalBalance } from '../../../../utils/model/balance-mensual'
import { getBalancesYear } from '../../../../utils/api/balances'
import css from './Centro.module.css'

const Balance = ({ balance, revertir = false, sufijo = '€' }) => {
  if (isNaN(balance)) return '--'
  if ((!revertir && balance >= 0) || (revertir && balance <= 0))
    return (
      <span className={css.balance} data-status='positive'>
        {format(balance)} {sufijo}
      </span>
    )
  else
    return (
      <span className={css.balance} data-status='negative'>
        {format(balance)} {sufijo}
      </span>
    )
}

const Importe = ({ importe, sufijo = '€' }) => (
  <span className={css.importe}>
    {format(importe)} {sufijo}
  </span>
)

const Centro = () => {
  const notification = useNotification()

  const [ejercicio, setEjercicio] = useState(new Date().getFullYear())
  const [centro, setCentro] = useState(getAuth().centro)

  const { isLoading: isLoadingCentro, data: balanceList = [] } = useQuery({
    queryKey: ['balance-mensual', 'list', 'year', ejercicio, centro],
    queryFn: () =>
      getBalancesYear({ periodo: ejercicio, centro })
        .then(datos =>
          datos.map(balance => ({
            ...balance,
            resultado: balance.ventaTeorica
              ? (100 * (balance.deudaMes || 0)) / balance.ventaTeorica
              : null
          }))
        )
        .catch(err => {
          notification.error({
            title: 'Error al recuperar los balances mensuales',
            content: err
          })
          return []
        })
  })

  const handleExport = () => {
    const content = getCSVContent({
      columns: [
        'Mes',
        'Ventas',
        'Servicios',
        'Deuda Ventas',
        'Deuda Servicios',
        'Ingresos',
        'Ingresos Desasignados',
        'Resultado'
      ],
      data: balanceList,
      transform: balance => [
        dateToMonth(balance.fecha),
        formatForExport(balance.ventas),
        formatForExport(balance.ventaTeorica),
        formatForExport(balance.deuda),
        formatForExport(balance.deudaMes),
        formatForExport(balance.ingresos),
        formatForExport(balance.ingresosDesasignados),
        formatForExport(balance.resultado)
      ]
    })
    downloadCSVFile(content, 'ventas-centro')
  }

  return (
    <PageLoading isLoading={isLoadingCentro}>
      <TableHeader
        actions={
          <div className={css.actions}>
            <AcademiaSelector
              name='academia'
              label='Academia'
              value={centro}
              onChange={e => setCentro(e.target.value)}
            />
            <EjercicioSelector
              className={css.ejercicio}
              name='ejercicio'
              label='Ejercicio'
              value={ejercicio}
              onChange={e => setEjercicio(e.target.value)}
              minYear={2015}
            />
            <IconButton
              className={css.download}
              size='small'
              color='primary'
              onClick={handleExport}
            >
              <i className='material-icons'>download</i>
            </IconButton>
          </div>
        }
        title='Balances mensuales del centro'
        showSearch={false}
      />
      <InformationTable
        details={[
          { title: 'Mes', key: 'mes', sortDisabled: true, align: 'left' },
          {
            title: 'Ventas',
            key: 'venta',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Deuda Ventas',
            key: 'deuda',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Servicios',
            key: 'venta_teorica',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Deuda Servicios',
            key: 'deuda_mes',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Ingresos',
            key: 'ingresos',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Desasignados',
            key: 'desasignados',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Resultado',
            key: 'resultado',
            sortDisabled: true,
            align: 'center'
          }
        ]}
        data={[...balanceList, getTotalBalance(balanceList)].map(balance => ({
          className: balance.fecha === 'Total' ? css.totalRow : null,
          mes:
            balance.fecha === 'Total' ? (
              <strong>{balance.fecha}</strong>
            ) : (
              dateToMonth(balance.fecha)
            ),
          venta: <Balance balance={balance.ventas} />,
          venta_teorica: <Balance balance={balance.ventaTeorica} />,
          deuda: <Balance revertir balance={balance.deuda} />,
          deuda_mes: <Balance revertir balance={balance.deudaMes} />,
          ingresos: <Importe importe={balance.ingresos} />,
          desasignados: <Importe importe={balance.ingresosDesasignados} />,
          resultado: <Balance revertir balance={balance.resultado} sufijo='%' />
        }))}
      />
    </PageLoading>
  )
}

export default Centro
